/**
 * /doctor-to-hotel-vietnam
 *
 * Service-level landing page targeting "doctor to hotel Vietnam" search
 * intent. Aggregates the doctor-to-hotel proposition across cities, with
 * coverage map linking back to live + expanding city pages.
 */
import React from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import {
  UrgentTrustStrip, UrgentIntro, UrgentCardGrid, UrgentSteps,
  UrgentFaq, UrgentEditorial, UrgentCtaBand, UrgentCoverageMap,
  CoordinationDisclaimer, RelatedServices, UrgentConditionsServices,
} from '../components/UrgentCareSections';
import { UrgentActionButtons } from '../components/UrgentSosContact';
import { Stethoscope, Languages, Siren } from 'lucide-react';
import { DOCTOR_TO_HOTEL } from '../data/urgentCareContent';
import { useCatalog } from '../context/CatalogContext';
import { getInfoLandingImage } from '../lib/imageOverrides';

const HERO_IMAGE = 'https://images.unsplash.com/photo-1666214280391-8ff5bd3c0bf0?auto=format&fit=crop&w=1400&q=80';
const HERO_FALLBACK = 'https://images.pexels.com/photos/4173251/pexels-photo-4173251.jpeg?auto=compress&cs=tinysrgb&w=1400';

const SITE = 'https://vietclinics.com';

const DoctorToHotelVietnamPage = () => {
  const c = DOCTOR_TO_HOTEL;
  const { overrides } = useCatalog();
  const heroImage = getInfoLandingImage(overrides, 'doctor-to-hotel', HERO_IMAGE);
  const canonical = `${SITE}${c.slug}`;

  const liveCities = [
    { slug: 'hoi-an', name: 'Da Nang & Hoi An', href: '/urgent-medical-care-hoi-an' },
  ];
  const expandingCities = [
    { slug: 'ho-chi-minh', name: 'Ho Chi Minh City', href: '/urgent-medical-care-ho-chi-minh' },
    { slug: 'hanoi',       name: 'Hanoi',             href: '/urgent-medical-care-hanoi' },
    { slug: 'nha-trang',   name: 'Nha Trang',         href: '/urgent-medical-care-nha-trang' },
    { slug: 'phu-quoc',    name: 'Phu Quoc',          href: '/urgent-medical-care-phu-quoc' },
  ];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': `${SITE}#organization`,
        name: 'VietClinics',
        alternateName: 'VietClinics — Healthcare Coordination',
        url: SITE,
        description:
          'VietClinics is an independent healthcare coordination and patient-support service that '
          + 'connects international visitors in Vietnam with licensed local doctors, clinics and '
          + 'hospitals. VietClinics is not a hospital, clinic, ambulance or emergency department and '
          + 'does not diagnose or treat patients itself.',
        email: 'support@vietclinics.com',
        telephone: '+84386492508',
        areaServed: { '@type': 'Country', name: 'Vietnam' },
      },
      {
        '@type': 'Service',
        '@id': `${canonical}#service`,
        serviceType: 'Doctor-to-hotel visit coordination',
        name: 'Doctor-to-hotel visit coordination in Vietnam',
        provider: { '@id': `${SITE}#organization` },
        areaServed: [
          { '@type': 'City', name: 'Da Nang', addressCountry: 'VN' },
          { '@type': 'City', name: 'Hoi An', addressCountry: 'VN' },
          { '@type': 'Country', name: 'Vietnam' },
        ],
        description: c.metaDescription,
        url: canonical,
        hoursAvailable: {
          '@type': 'OpeningHoursSpecification',
          description: 'Enquiry line open 24 hours a day. In-person visit times depend on city, hour and provider confirmation.',
        },
        hasOfferCatalog: {
          '@type': 'OfferCatalog',
          name: 'In-room doctor visit — what we coordinate',
          itemListElement: c.covered.map((s) => ({
            '@type': 'Offer',
            itemOffered: { '@type': 'Service', name: s.title, description: s.body },
          })),
        },
      },
      {
        '@type': 'WebPage',
        '@id': `${canonical}#webpage`,
        url: canonical,
        name: c.metaTitle,
        description: c.metaDescription,
        inLanguage: 'en',
        dateModified: c.editorial.lastUpdated,
        lastReviewed: c.editorial.lastUpdated,
        reviewedBy: { '@type': 'Organization', name: c.editorial.reviewedBy },
        about: { '@id': `${SITE}#organization` },
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${canonical}#breadcrumb`,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE },
          { '@type': 'ListItem', position: 2, name: 'Urgent Medical Care', item: `${SITE}/urgent-medical-care` },
          { '@type': 'ListItem', position: 3, name: 'Doctor to Hotel', item: canonical },
        ],
      },
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{c.metaTitle}</title>
        <meta name="description" content={c.metaDescription} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={c.metaTitle} />
        <meta property="og:description" content={c.metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={heroImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <Header />

      <section className="relative overflow-hidden bg-white" data-testid="d2h-hero">
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(135deg, #F0FAF8 0%, #DCEFEC 60%, #ffffff 100%)',
          }}
        />
        <div className="relative container mx-auto px-4 max-w-7xl py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[11px] font-bold text-[#1A8F8A] uppercase tracking-widest mb-4 inline-block bg-white/80 backdrop-blur px-3 py-1.5 rounded-full border border-[#DCEFEC]">
                {c.hero.eyebrow}
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight leading-[1.05] mb-5">
                {c.hero.title}
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed max-w-xl mb-7">
                {c.hero.subtitle}
              </p>
              <div className="mb-6" data-testid="d2h-hero-actions">
                <UrgentActionButtons placement="d2h-hero" />
              </div>
              <div className="max-w-xl">
                <CoordinationDisclaimer testId="d2h-disclaimer" />
              </div>
            </div>
            <div className="relative rounded-3xl overflow-hidden bg-slate-100" style={{ aspectRatio: '4 / 3' }}>
              <img
                src={heroImage}
                onError={(e) => { e.currentTarget.src = HERO_FALLBACK; }}
                alt="In-hotel doctor visit coordinated by VietClinics"
                className="w-full h-full object-cover"
                loading="eager"
              />
            </div>
          </div>
        </div>
      </section>

      <UrgentTrustStrip />

      <UrgentIntro heading={c.intro.heading} paragraphs={c.intro.paragraphs} testId="d2h-intro" />

      <UrgentCardGrid
        eyebrow="What is covered"
        heading="In-room doctor visit — what we coordinate"
        items={c.covered}
        testId="d2h-covered"
      />

      <UrgentConditionsServices testId="d2h-conditions" />

      <UrgentSteps
        eyebrow="How it works"
        heading="From enquiry to in-room visit"
        steps={c.howItWorks}
        testId="d2h-steps"
      />

      <UrgentCoverageMap
        liveCities={liveCities}
        expandingCities={expandingCities}
        showServices={false}
        testId="d2h-coverage"
      />

      <RelatedServices
        heading="Related VietClinics services"
        links={[
          { to: '/urgent-medical-care', icon: Stethoscope, title: 'Urgent medical care hub', body: 'All non-emergency urgent options in Vietnam — services, coverage and how enquiries work.' },
          { to: '/english-speaking-doctor-vietnam', icon: Languages, title: 'English-speaking doctor', body: 'Hotel, clinic and same-day online consultations with English-speaking clinicians.' },
          { to: '/emergency', icon: Siren, title: 'Emergency hotlines', body: 'Life-threatening emergency? Call 115 for an ambulance or go to the nearest hospital.' },
        ]}
        testId="d2h-related"
      />

      <UrgentFaq
        heading="Doctor-to-hotel in Vietnam — frequently asked"
        items={c.faq}
        testId="d2h-faq"
      />

      <UrgentEditorial editorial={c.editorial} testId="d2h-editorial" />

      <UrgentCtaBand
        heading="Need a doctor at your hotel in Vietnam?"
        subheading="Tell us your hotel and your symptoms — we will respond with available options and an indicative quote."
        testId="d2h-cta"
      />

      <Footer />
    </div>
  );
};

export default DoctorToHotelVietnamPage;
